import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { vehiclePrimaryImage } from '@/utils/vehicleImages';
import { vehicleMatchesSearch } from '@/utils/vehicleSearch';
import { formatVehiclePrice } from '@/utils/vehiclePrice';
import { formatDateMdY } from '@/utils/dateFormat';
import AdminPageHeader from '@/components/admin/AdminPageHeader';
import AdminRecordCard from '@/components/admin/AdminRecordCard';
import AdminFilterBar from '@/components/admin/AdminFilterBar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function SoldVehiclesPage() {
  const navigate = useNavigate();
  const { token, isAuthenticated } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchSold = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/vehicles`, {
        params: { status: 'sold' },
        headers: { Authorization: `Bearer ${token}` },
      });
      setVehicles(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('Failed to load sold vehicles');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (isAuthenticated && token) fetchSold();
  }, [isAuthenticated, token, fetchSold]);

  const filtered = vehicles.filter((v) => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return true;
    const buyer = (v.sale?.customer_name || '').toLowerCase();
    return vehicleMatchesSearch(v, q, [buyer]);
  });

  return (
    <div className="p-4 sm:p-6 max-w-[1400px] mx-auto" data-testid="sold-vehicles-page">
      <AdminPageHeader
        title="Sold Vehicles"
        subtitle="Vehicles marked as sold. Open a record for customer and vehicle details."
        backTo="/admin/inventory"
        backLabel="Inventory"
      />

      <AdminFilterBar
        search={searchTerm}
        onSearch={setSearchTerm}
        searchPlaceholder="Search by vehicle, VIN, or customer name..."
      />

      <div className="bg-white/5 border border-white/10 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-white/60 font-['Manrope']">Loading sold vehicles...</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-white/60 font-['Manrope']">No sold vehicles</div>
        ) : (
          <>
          <div className="lg:hidden space-y-3 p-3">
            {filtered.map((vehicle) => (
              <AdminRecordCard
                key={vehicle.id}
                title={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                subtitle={vehicle.vin}
                fields={[
                  { label: 'Customer', value: vehicle.sale?.customer_name || '—' },
                  { label: 'Sold', value: vehicle.sold_at ? formatDateMdY(vehicle.sold_at) : '—' },
                  {
                    label: 'Purchase',
                    value: [
                      vehicle.sale?.purchase_type,
                      vehicle.sale?.sale_price != null
                        ? `$${Number(vehicle.sale.sale_price).toLocaleString()}`
                        : formatVehiclePrice(vehicle),
                    ].filter(Boolean).join(' · ') || '—',
                  },
                ]}
                onClick={() => navigate(`/admin/sold-vehicles/${vehicle.id}`)}
                primary={
                  <button
                    type="button"
                    onClick={() => navigate(`/admin/sold-vehicles/${vehicle.id}`)}
                    className="w-full min-h-11 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold"
                  >
                    Open
                  </button>
                }
              />
            ))}
          </div>
          <table className="hidden lg:table w-full">
            <thead className="border-b border-white/10">
              <tr className="text-left text-xs uppercase tracking-wider text-white/40 font-['Manrope']">
                <th className="px-6 py-3">Vehicle</th>
                <th className="px-6 py-3">Customer</th>
                <th className="px-6 py-3">Purchase</th>
                <th className="px-6 py-3">Sold</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {filtered.map((vehicle) => (
                <tr
                  key={vehicle.id}
                  className="hover:bg-white/5 cursor-pointer"
                  onClick={() => navigate(`/admin/sold-vehicles/${vehicle.id}`)}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <img
                        src={vehiclePrimaryImage(vehicle)}
                        alt=""
                        className="w-16 h-12 object-cover"
                      />
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="font-['Manrope'] font-semibold text-white">
                            {vehicle.year} {vehicle.make} {vehicle.model}
                          </div>
                          {(Number(vehicle.linked_deal_count) > 0 || vehicle.sale?.deal_id) && (
                            <span className="px-2 py-0.5 text-[10px] font-['Manrope'] font-semibold uppercase tracking-wide bg-cyan-500/15 text-cyan-300 border border-cyan-500/30">
                              {Number(vehicle.linked_deal_count) > 1
                                ? `Linked to ${vehicle.linked_deal_count} Deals`
                                : 'Linked to Deal'}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-white/50 font-['Manrope']">{vehicle.vin}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 font-['Manrope'] text-white">
                    {vehicle.sale?.customer_name || '—'}
                  </td>
                  <td className="px-6 py-4 font-['Manrope'] text-white/80 capitalize">
                    {vehicle.sale?.purchase_type || '—'}
                    {vehicle.sale?.sale_price != null
                      ? ` · $${Number(vehicle.sale.sale_price).toLocaleString()}`
                      : formatVehiclePrice(vehicle)
                        ? ` · ${formatVehiclePrice(vehicle)}`
                        : ''}
                  </td>
                  <td className="px-6 py-4 font-['Manrope'] text-white/70">
                    {vehicle.sold_at ? formatDateMdY(vehicle.sold_at) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
        )}
      </div>
    </div>
  );
}
